import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, BookOpen, UserCheck, Users, Newspaper, Briefcase, Calendar, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function RetentionPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-red-50/20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-red-100/20 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-500">Programs</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Retention</span>
            </nav>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-200 mb-6">
              <Heart className="w-4 h-4 text-brand-red" />
              <span className="text-sm font-medium text-red-700">Retention Program</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Thriving Together:{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-red to-red-600">
                Essential Tools for Success
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              We understand that having the right resources is crucial for your success. That's why we have curated a comprehensive set of tools and materials designed to support your journey with us.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">Resources for Our Members</h2>
            <p className="text-navy-600">Access a comprehensive set of tools and materials designed to support your journey.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-red-500/20">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2">Educational Workshops</h3>
                <p className="text-navy-600 text-sm leading-relaxed">Access to a variety of workshops aimed at enhancing your skills and knowledge.</p>
              </CardContent>
            </Card>
            
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-gold-500/20">
                  <Briefcase className="w-6 h-6 text-navy-900" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2">Professional Development</h3>
                <p className="text-navy-600 text-sm leading-relaxed">Tailored programs to help you advance in your career.</p>
              </CardContent>
            </Card>
            
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-green-500/20">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2">Community Engagement</h3>
                <p className="text-navy-600 text-sm leading-relaxed">Participate in events and activities that build connections and foster growth.</p>
              </CardContent>
            </Card>
            
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-navy to-navy-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-navy-500/20">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2">Mentorship Programs</h3>
                <p className="text-navy-600 text-sm leading-relaxed">Benefit from guidance and support through our structured mentorship initiatives.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Onboarding Section */}
      <section className="py-24 bg-gradient-to-br from-brand-red to-red-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Onboarding New Members</h2>
              <p className="text-red-100 text-lg leading-relaxed mb-8">
                Welcoming new members is a vital part of our community's growth and success. Our onboarding process is designed to ensure that you feel integrated and supported from day one.
              </p>
              
              <div className="space-y-4">
                {[
                  { title: 'Orientation Sessions', desc: 'Get acquainted with our mission, values, and programs' },
                  { title: 'Mentor Assignments', desc: 'Get paired with experienced mentors' },
                  { title: 'Resource Kits', desc: 'Essential information and materials to help you navigate' },
                  { title: 'Networking Opportunities', desc: 'Engage with other members through meet-and-greet events' },
                ].map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-red-100 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Image */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-4 border border-white/20 overflow-hidden">
              <img 
                src="/images/retention-photo.jpg" 
                alt="DC Men in ECE Community"
                className="w-full h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stay Up to Date */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">Stay Up to Date</h2>
            <p className="text-navy-600">Continuous learning and staying informed are key components of our retention efforts.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-gold-500/20">
                  <Newspaper className="w-7 h-7 text-navy-900" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Newsletters</h3>
                <p className="text-navy-600 leading-relaxed">Regular updates featuring the latest news, strategies, and opportunities within our community.</p>
              </CardContent>
            </Card>
            
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-green-500/20">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Social Media</h3>
                <p className="text-navy-600 leading-relaxed">Follow us on social media to get real-time updates and insights from our community.</p>
              </CardContent>
            </Card>
            
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-red-500/20">
                  <Calendar className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Webinars & Workshops</h3>
                <p className="text-navy-600 leading-relaxed">Participate in ongoing educational sessions to keep your skills sharp.</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/conference">
              <Button size="lg" className="bg-brand-red hover:bg-red-600 text-white font-semibold px-8 shadow-xl shadow-red-500/20">
                Learn About Our Conference
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-navy-50 to-red-50/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Join Our Supportive Community
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            Become part of a network that helps you thrive in your career.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-xl shadow-gold-500/25">
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/advancement">
              <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-white">
                Explore Advancement
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
